import React, { useEffect, useState } from "react";

import {
  GetFacilityPermissions,
  IFacilityRole,
  IPermissionDetails,
  IPermissionDetailsList,
  IPermissionWithPivot,
  IPostFacilityRoleParams,
  PostFacilityRole,
  PutFacilityRole,
} from "api/rpc/2024-04/facilityAdmin/client/admin/admin";

import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import RoleDetailsCard from "./components/RoleDetailsCard";
import { useHistory } from "react-router";
import useModal from "hooks/modals/useModal";
import Popup from "components/popup/Popup";
import PermissionEditor from "../permissions/PermissionEditor";

export default function FacilityStaffDetailsEdit() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { state: kioskViewPopup, updateModal: updateKioskViewPopup, closeModal: closeKioskViewPopup } = useModal();

  const [roleState, setRoleState] = useState<IFacilityRole>({
    title: "",
    description: "",
    default: false,
    permissions: [],
  });
  const [permissionState, setPermissionState] = useState<IPermissionDetailsList>(null);
  const [addWage, setAddWage] = useState<boolean>(false);

  useEffect(() => {
    void loadPermissionDetails();
  }, []);

  useEffect(() => {
    const allPermissions: IPermissionWithPivot[] = [];
    if (permissionState) {
      Object.values(permissionState as Record<string, unknown>).forEach(category => {
        Object.values(category as Record<string, unknown>).forEach(permissions => {
          (permissions as IPermissionDetails[]).forEach(p => {
            allPermissions.push({
              ...p,
              pivot: {
                permission_id: p.id,
                boolean_value: false,
              },
            });
          });
        });
      });
    }

    setRoleState(prev => ({
      ...prev,
      permissions: allPermissions,
    }));
  }, [permissionState]);

  // API
  async function loadPermissionDetails() {
    const permissionDetails = await GetFacilityPermissions({}, true);

    if (permissionDetails.status !== StatusCode.OK) {
      dispatch(showError("Error retrieving permissions"));
      return;
    }
    setPermissionState(permissionDetails.data);
  }

  async function createNewRole() {
    const permissionsData = roleState.permissions?.map(permission => ({
      permission_detail_id: permission.id,
      value_type: "boolean",
      value: permission.pivot.boolean_value,
    }));
    const params: IPostFacilityRoleParams = {
      title: roleState?.title,
      description: roleState?.description,
      default: roleState?.default,
      wage: addWage ? roleState?.wage : undefined,
    };

    const roleRes = await PostFacilityRole(params, true);
    if (roleRes.status !== StatusCode.OK) {
      dispatch(showError("Error creating new role"));
      return;
    }

    const updatePermissionParams = {
      id: roleRes.data.id,
      permissions: permissionsData,
    };

    const updatePermissionRes = await PutFacilityRole(updatePermissionParams, true);

    if (updatePermissionRes.status !== StatusCode.OK) {
      dispatch(showError("Error updating permissions"));
      return;
    }

    dispatch(showSuccess("New role successfully created"));
    void closeKioskViewPopup();
    history.push("/admin/settings/staff/");
  }
  // End API

  // Handlers
  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setRoleState(e => ({ ...e, [id]: value }));
  }

  function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setRoleState(prev => ({ ...prev, [id]: checked }));
  }

  function setRolePermission(permission: IPermissionDetails, checked: boolean) {
    setRoleState(prev => {
      const exists = prev.permissions?.some(item => item.id === permission.id);
      const permissions = exists
        ? prev.permissions?.map(item =>
            item.id === permission.id
              ? { ...item, pivot: { ...item.pivot, boolean_value: checked, permission_id: permission.id } }
              : item,
          )
        : [
            ...(prev.permissions || []),
            { ...permission, pivot: { permission_id: permission.id, boolean_value: checked } },
          ];

      return { ...prev, permissions };
    });
  }

  function setRolePermissions(permissionList: IPermissionDetails[], checked: boolean) {
    const ids = new Set(permissionList.map(permission => permission.id));

    setRoleState(prev => {
      const updated = (prev.permissions || []).map(permission =>
        ids.has(permission.id)
          ? { ...permission, pivot: { ...permission.pivot, boolean_value: checked } }
          : permission,
      );
      const existingIds = new Set(updated.map(permission => permission.id));
      const missing = permissionList
        .filter(permission => !existingIds.has(permission.id))
        .map(permission => ({ ...permission, pivot: { permission_id: permission.id, boolean_value: checked } }));

      return { ...prev, permissions: [...updated, ...missing] };
    });
  }

  function checkStatus(permission: IPermissionDetails) {
    return roleState?.permissions?.some(item => item.id === permission.id && item.pivot.boolean_value) ?? false;
  }

  function roleHasKioskViewEnabled() {
    return roleState?.permissions?.some(permission => permission?.key === "kiosk_view" && !!permission?.pivot?.boolean_value) ?? false;
  }

  function handleCreateRole() {
    if (roleHasKioskViewEnabled()) {
      void updateKioskViewPopup({ isOpen: true });
      return;
    }
    void createNewRole();
  }

  return (
    <Page
      title={t("Create New Facility Role")}
      subtitle={t("Set up access permissions and manage staff assignments for this role.")}
      primaryAction={{
        action: () => handleCreateRole(),
        content: t("Create Role"),
        disabled: !roleState?.title,
      }}
      breadcrumbs={[
        { label: t("Settings") },
        { label: t("Staff Accounts"), url: "/admin/settings/staff" },
        { label: t("Create New Facility Role"), url: "/admin/settings/roles_new" },
      ]}
    >
      <div className="mb-10">
        <RoleDetailsCard addWage={addWage} updateAddWage={() => setAddWage(prev => !prev)} roleDetails={roleState} inputChange={handleInputChange} toggleChange={handleCheckboxChange} />
        <PermissionEditor
          permissions={permissionState}
          title={t("Role Permissions")}
          isChecked={checkStatus}
          onToggle={setRolePermission}
          onToggleMany={setRolePermissions}
        />
      </div>
      <Popup
        open={kioskViewPopup?.isOpen}
        type="info"
        title={t("Kiosk View Permission Set")}
        description={t("This role will only have access to the kiosk view. Are you sure you want to continue?")}
        onOk={createNewRole}
        okText={t("Create Role")}
        cancelText={t("Cancel")}
        onCancel={closeKioskViewPopup}
      />
    </Page>
  );
}
